import { Injectable } from '@angular/core';
import {
  AyaSchoolDataByLanguage,
  DestinationFilter,
  JuniorCampInfo,
  JuniorPriceList,
  JuniorPriceLists,
  PriceList,
  YoungAdultsPriceList,
  YoungAdultsPriceListsFromApi,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class FilterService {
  getSchoolPriceListFilters(priceLists: PriceList[]): DestinationFilter[] {
    return priceLists?.reduce<DestinationFilter[]>(
      (filters: DestinationFilter[], { country: { code: countryCode, name: countryName }, name, code }: PriceList) => {
        return this.addFilterNodesToFilters(filters, countryCode, countryName, code, name);
      },
      [],
    );
  }

  /**
   * It gets the destination filters for junior camps from the price lists.
   * @param priceLists the junior price lists containing junior camp information from where to extract the information
   * to get the filters
   */
  getJuniorCampDestinationFilters(priceLists: JuniorPriceLists): DestinationFilter[] {
    return priceLists?.camps
      ?.flatMap((camp: JuniorPriceList) => camp.juniorCampInfo)
      ?.reduce<DestinationFilter[]>(
        (
          filters: DestinationFilter[],
          { country: { code: countryCode, name: countryName }, school: { code: schoolCode, name: schoolName } }: JuniorCampInfo,
        ) => {
          return this.addFilterNodesToFilters(filters, countryCode, countryName, schoolCode, schoolName);
        },
        [],
      );
  }

  /**
   * It gets the destination filters for young adults camps from the price lists.
   * @param priceLists the young adults price lists containing young adults schools information from where to extract the information
   * to get the filters
   */
  getYoungAdultsSchoolsDestinationFilters(priceLists: YoungAdultsPriceListsFromApi): DestinationFilter[] {
    return priceLists?.schools
      ?.flatMap((school: YoungAdultsPriceList) => school.youngAdultSchoolInfo)
      ?.reduce<DestinationFilter[]>(
        (filters: DestinationFilter[], { country: { code: countryCode, name: countryName }, school: { code: schoolCode, name: schoolName } }) => {
          return this.addFilterNodesToFilters(filters, countryCode, countryName, schoolCode, schoolName);
        },
        [],
      );
  }

  /**
   * It gets the destination filters for AYA schools from the price lists.
   * @param ayaSchoolsByCountryTables the AYA schools by country tables containing school information from where to extract the information
   * to get the filters
   */
  getAyaDestinationFilters(ayaSchoolsByCountryTables: AyaSchoolDataByLanguage[]): DestinationFilter[] {
    // flat map the schools by country tables to get all schools grouped by country
    const flatten = ayaSchoolsByCountryTables.flatMap(({ schoolsByCountryTable }) => schoolsByCountryTable);

    return flatten?.reduce<DestinationFilter[]>((filters, { country: { code: countryCode, name: countryName }, schools }) => {
      schools.forEach(({ schoolCode, schoolName }) => {
        filters = this.addFilterNodesToFilters(filters, countryCode, countryName, schoolCode, schoolName);
      });
      return filters;
    }, []);
  }

  /**
   * Auxiliary function to add filter nodes to the filters list from a given country code, country name, school code and school name.
   */
  private addFilterNodesToFilters(filters: DestinationFilter[], countryCode: string, countryName: string, code: string, name: string): DestinationFilter[] {
    const newFilterList = [...filters];

    let countryFilter = newFilterList.find(({ key }) => key === countryCode);
    if (!countryFilter) {
      countryFilter = {
        key: countryCode,
        label: countryName,
        data: 'country',
        children: [],
      };
      newFilterList.push(countryFilter);
    }
    const schoolFilter = countryFilter.children.some(({ key }) => key === code);
    if (!schoolFilter) {
      countryFilter.children.push({
        key: code,
        label: name,
        data: 'school',
      });
    }
    return newFilterList;
  }
}
